// Six single-digit boxes with paste + arrow/backspace handling. Shared by
// CheckInbox and StepUpModal. Controlled via `value` (digits only).
import React, { useRef } from 'react';

export default function CodeBoxes({ value, onChange, disabled, autoFocus = true, label = 'Digit' }) {
  const refs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value.charAt(i));

  const set = (next) => onChange(next.replace(/\D/g, '').slice(0, 6));
  const focus = (i) => refs.current[Math.max(0, Math.min(5, i))]?.focus();

  const fill = (from, text) => {
    const clean = text.replace(/\D/g, '');
    if (!clean) return;
    const arr = digits.slice();
    for (let k = 0; k < clean.length && from + k < 6; k += 1) arr[from + k] = clean.charAt(k);
    set(arr.join(''));
    focus(from + clean.length);
  };

  return (
    <div className="flex items-center justify-center gap-2" role="group" aria-label="6-digit code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="w-10 h-12 text-center text-lg rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`${label} ${i + 1}`}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          value={d}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, '');
            if (!typed) {
              const arr = digits.slice(); arr[i] = ''; set(arr.join(''));
              return;
            }
            fill(i, typed);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[i] && i > 0) {
              const arr = digits.slice(); arr[i - 1] = ''; set(arr.join('')); focus(i - 1);
            } else if (e.key === 'ArrowLeft') focus(i - 1);
            else if (e.key === 'ArrowRight') focus(i + 1);
          }}
          onPaste={(e) => {
            const text = (e.clipboardData || window.clipboardData).getData('text') || '';
            if (!/\d/.test(text)) return;
            e.preventDefault();
            fill(0, text);
          }}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
